/**
 * Composition root.
 *
 * This is the ONE file in the application permitted to import a vendor SDK;
 * the ESLint config enforces that for every other file under src/. Swapping
 * the database later is a change here and nowhere else.
 */
import { createSeededMemoryPorts, type MemoryPorts } from '@buildmate/adapter-memory';
import { systemClock, type Ports } from '@buildmate/core';

export type AdapterName = 'memory' | 'firebase';

export function selectedAdapter(): AdapterName {
  return import.meta.env.VITE_ADAPTER === 'firebase' ? 'firebase' : 'memory';
}

async function createFirebasePorts(): Promise<Ports> {
  // Imported dynamically so a build running on the in-memory adapter never
  // ships the Firebase SDK. Statically imported it added ~380 kB to every
  // bundle, including ones that cannot reach a database at all.
  const {
    connectFirebase,
    FirestoreDataPort,
    FirebaseAuthPort,
    FirebaseFunctionsPort,
    FirebaseStoragePort,
  } = await import('@buildmate/adapter-firebase');
  const { createMemoryPorts } = await import('@buildmate/adapter-memory');

  const useEmulators = import.meta.env.VITE_USE_EMULATORS !== 'false';
  const { app, db, auth, storage } = connectFirebase({
    config: {
      // Every default targets the local emulator suite, so a missing env file
      // fails safe: the app points at demo-buildmate, which never reaches
      // Google's servers, rather than silently writing to a real project.
      apiKey: import.meta.env.VITE_FIREBASE_API_KEY ?? 'demo-api-key',
      projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID ?? 'demo-buildmate',
      authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ?? 'demo-buildmate.firebaseapp.com',
      storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET ?? 'demo-buildmate.appspot.com',
      messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
      appId: import.meta.env.VITE_FIREBASE_APP_ID,
    },
    useEmulators,
  });

  // Push is still an in-memory stand-in; its Firebase implementation lands
  // with mobile. Storage is real as of the file pipeline. The port boundary
  // is what makes running a mixed set possible at all.
  const stubs = createMemoryPorts();

  return {
    data: new FirestoreDataPort(db),
    auth: new FirebaseAuthPort(auth, db),
    functions: new FirebaseFunctionsPort(app, { useEmulators }),
    storage: new FirebaseStoragePort(storage),
    push: stubs.push,
    clock: systemClock,
  };
}

export async function createPorts(): Promise<Ports | MemoryPorts> {
  return selectedAdapter() === 'firebase' ? createFirebasePorts() : createSeededMemoryPorts();
}
